// ManDana — the shared chrome: the bar a section folds under, and the mark a
// screen wears on the way out.
//
// ── the section bar ─────────────────────────────────────────────────────────
//
// A rounded bar carrying a micro-label, the count it already knew, and a
// chevron that turns a quarter when the section under it is open.
//
// The compose sheet's Details panel wrote this first and got it right. Tabs
// wrote its Recents and Favorites bars separately — a 1px stroke, radius 11,
// a sentence-case label, and a TEXT chevron ("›" and "⌄"), which is two more
// glyphs than this app has and neither of them its own. Two authors is how one
// component quietly becomes two, and the second one is always the one nobody
// re-reads.
//
// WHY ITS OWN FILE rather than md-theme beside `mdFrostDress`. The bar needs
// three things from the palette — `ink`, `mdIsLight`, and the chevron — and
// nothing else. Half the sheet suites deliberately stub exactly those three
// because "the real theme drags a whole app boot behind it for a colour
// nothing here asserts on" (info-panel.test.js). Put the bar inside md-theme
// and those suites can only reach it by booting the app or by faking the very
// component they are asserting on. Here it is one cheap module they load for
// real, over a stubbed palette — which is what they were always doing.
//
// The STROKE is gone by instruction (Marwan, 2026-08-24): the ground is a tint
// of the ink, so the bar reads on any surface without a line drawn round it,
// and a column of them reads as structure instead of as a stack of boxes.

function mdSectionBarGround(light, hot) {
  if (hot) return window.ink(light ? 0.07 : 0.1);
  return window.ink(light ? 0.04 : 0.06);
}

function mdSectionBarDress(light) {
  return {
    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
    minHeight: 32, padding: '0 9px 0 11px', border: 'none', borderRadius: 10,
    background: mdSectionBarGround(light, false), color: 'inherit', font: 'inherit',
    textAlign: 'start', cursor: 'pointer', transition: 'background .14s ease',
  };
}

/* Hover is written straight onto the node rather than held as state: a bar
   that re-renders its whole section to shade itself is a fold that stutters
   under the pointer. */
/* The icon is named, not passed. Six sections across two surfaces each handing
   in their own svg is precisely the drift this component was written to end:
   the bar decides the size and the ink, the caller only says WHICH. A name MDI
   does not know draws nothing rather than throwing — a missing glyph should
   cost a section its icon, never its heading. */
function MdSectionBar({ icon, label, count = 0, open, onToggle, style, ...rest }) {
  const ink = window.ink;
  const light = window.mdIsLight();
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  const chevron = (window.MDI && window.MDI.chevron) || (() => null);
  const glyph = (icon && window.MDI && window.MDI[icon]) || null;
  const wash = (hot) => (event) => {
    event.currentTarget.style.background = mdSectionBarGround(light, hot);
  };
  return <button type="button" onClick={onToggle} aria-expanded={open ? 'true' : 'false'}
    style={{ ...mdSectionBarDress(light), ...style }}
    onPointerEnter={wash(true)} onPointerLeave={wash(false)} {...rest}>
    {glyph ? glyph(ink(0.45), 13) : null}
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.7,
      textTransform: 'uppercase', color: ink(0.5) }}>{label}</span>
    {!!count && <span style={{ fontSize: 11, fontWeight: 650, color: ink(0.36),
      fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
    <span aria-hidden="true" style={{ marginInlineStart: 'auto', display: 'grid',
      placeItems: 'center', width: 16, height: 16,
      /* Down when it is open, pointing the way out when it is not — and "the
         way out" is the trailing side, which in Arabic is the other one. The
         rotation is the same in both; only the resting angle has a direction. */
      transform: open ? 'rotate(90deg)' : (rtl ? 'rotate(180deg)' : 'none'),
      transition: 'transform .18s ease' }}>{chevron(ink(0.42), 11)}</span>
  </button>;
}

/* ── the way out ─────────────────────────────────────────────────────────────
   THE × WAS SAYING THREE THINGS (Marwan, 2026-08-24, polish 6): "Substitute all
   'X' glyphs with '-' glyph and put a circle hairline border around it. This
   applies to all Screens and windows not components like filters and etc."

   One glyph carried close-this-screen, take-this-tag-off, and delete-this-board
   — a refusal drawn identically for the harmless and the irreversible, so the
   destructive ones borrowed the calm of the harmless one. The dismissal takes a
   mark of its own: a minus, which removes nothing, inside a hairline ring that
   says the mark belongs to the SURFACE and not to anything standing on it. The
   × keeps every place it still means removal.

   THE RING IS PART OF THE DRAWING (Marwan, 2026-08-24, correcting the first
   cut): "the frame and the -, should merge and be treated as one whole glyph so
   the frame weight should be the same as the - glyph weight, then follow the
   grammar of ManDana's Glyphs in terms of size adaptations." The first cut put
   the ring on the button as a CSS border, which made it the size of the TARGET
   — 32 to 38px beside 19px neighbours — at 0.75px against the set's 1.8. Both
   halves now live in `MDI.dismiss`, at one weight, in one ink.

   So this component owns the INK and the TARGET, and the glyph set owns the
   mark. `size` is what `size` is everywhere else in this app: the size of the
   glyph, in the set's own units. The button around it is derived — 1.7× — and
   draws nothing at all, which is the whole correction: a hit area that painted
   a second ring around the ring was the thing that read as too large.

   1.7 rather than a fixed 44: the mark is the visible thing and the target
   should stay proportionate to it, and at the two sizes actually used that
   lands on 32 and 37, which are the boxes this chrome already had.

   No ground of its own. A filled tile inside a sheet head reads as a second
   surface laid on the first; a caller that genuinely floats over content — the
   phone desk, which has no chrome to stand on — passes its own frost through
   `style`, and gets the round shape for it from here. */
function mdDismissDress(size) {
  const box = Math.round(size * 1.7);
  return {
    width: box, height: box, flex: `0 0 ${box}px`,
    display: 'grid', placeItems: 'center', padding: 0,
    borderRadius: '50%', border: 'none',
    background: 'transparent', color: 'inherit', cursor: 'pointer',
  };
}

/* forwardRef because the stage takes focus to its way out when it opens
   (md-board.jsx), and a ref handed to a plain function component reaches
   nothing — silently, which is the worst way for a focus move to fail. */
const MdDismiss = React.forwardRef(function MdDismiss(
  { label, onClick, size = 19, style, ...rest }, ref) {
  const mark = (window.MDI && window.MDI.dismiss) || (() => null);
  return <button type="button" ref={ref} data-md-dismiss="true" aria-label={label} title={label}
    onClick={onClick} style={{ ...mdDismissDress(size), ...style }} {...rest}>
    {/* ink(0.55): the page controls the mark stands beside — the full-screen
        button in every window head — draw at this grey, and two greys in one
        row read as two kinds of control (polish 6 follow-up). */}
    {mark(window.ink(0.55), size)}
  </button>;
});

Object.assign(window, { MdSectionBar, mdSectionBarDress, mdSectionBarGround,
  MdDismiss, mdDismissDress });
